import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';

/**
 * Push-to-talk voice input wired to the OpenAI Realtime API (WebRTC).
 *
 * Why Realtime (and not just Whisper REST):
 *   The user gets character-by-character transcription deltas DURING speech
 *   from OpenAI, not the browser's local Web Speech engine. This means the
 *   live preview is the same quality as the final transcript — no jump on
 *   stop. We never expose the org's API key to the browser; the server
 *   mints a short-lived `ek_...` ephemeral client secret per session via
 *   /api/voice/realtime-token.
 *
 * Flow:
 *   1. Click mic.
 *   2. Fetch ephemeral key from /api/voice/realtime-token.
 *   3. Create RTCPeerConnection, attach mic track, attach data channel.
 *   4. SDP exchange POSTs the offer to
 *      https://api.openai.com/v1/realtime/calls?model=... with the ek_ key.
 *   5. Data channel `message` events stream transcription deltas
 *      (event.type === 'conversation.item.input_audio_transcription.delta')
 *      which we accumulate and feed back via onUpdate.
 *   6. Click mic again → close peer connection. Final completed event
 *      replaces interim text if present.
 *
 * Fallback: if any of (token mint, getUserMedia, SDP exchange) fails, we
 * drop to MediaRecorder → /api/voice/transcribe (Whisper REST), so the
 * button still works even when Realtime isn't reachable.
 *
 * Props:
 *   getCurrentText() — input value at start (preserved as prefix).
 *   onUpdate(text)   — combined prefix + speech-so-far on every delta.
 *   lang             — BCP47, default 'en-US'. Used only by the Whisper
 *                      fallback (Realtime auto-detects language).
 *   className        — passes through to the button element.
 */

function playUiTone(kind) {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  try {
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    const start = ctx.currentTime;
    const dur = 0.14;
    if (kind === 'start') {
      osc.frequency.setValueAtTime(660, start);
      osc.frequency.linearRampToValueAtTime(990, start + dur);
    } else {
      osc.frequency.setValueAtTime(660, start);
      osc.frequency.linearRampToValueAtTime(440, start + dur);
    }
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.18, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur + 0.02);
    osc.onended = () => { try { ctx.close(); } catch (_) { /* noop */ } };
  } catch (_) { /* noop */ }
}

/**
 * Mic input constraints — see VoiceModeOverlay for the full rationale.
 * Critical for iOS Safari which otherwise picks the bottom mic and runs AGC
 * hot, feeding noise into both paths (Realtime WebRTC + MediaRecorder fallback).
 */
const VOICE_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: 24000,
  channelCount: 1,
};

function pickRecordMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (_) { /* noop */ }
  }
  return '';
}

export default function VoiceMicButton({
  getCurrentText,
  onUpdate,
  onListeningChange,
  lang = 'en-US',
  className = '',
}) {
  // 'idle' | 'connecting' | 'recording' | 'transcribing' (last only for fallback)
  const [state, setState] = useState('idle');
  const [unsupported, setUnsupported] = useState(false);

  // OpenAI Realtime refs
  const pcRef = useRef(null);                 // RTCPeerConnection
  const dcRef = useRef(null);                 // data channel
  const mediaStreamRef = useRef(null);        // mic MediaStream
  const interimByItemRef = useRef({});        // per-item interim transcript
  const finalSoFarRef = useRef('');           // accumulated final transcripts

  // Whisper fallback refs
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  // Stable callbacks
  const onUpdateRef = useRef(onUpdate);
  const getCurrentTextRef = useRef(getCurrentText);
  const onListeningChangeRef = useRef(onListeningChange);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
    getCurrentTextRef.current = getCurrentText;
    onListeningChangeRef.current = onListeningChange;
  }, [onUpdate, getCurrentText, onListeningChange]);

  // Notify the parent whenever the recording state flips so it can run voice-mode
  // chrome (auto-quick mode, auto-speak responses, exit banner, etc.).
  useEffect(() => {
    if (state === 'recording') onListeningChangeRef.current?.(true);
    else if (state === 'idle') onListeningChangeRef.current?.(false);
  }, [state]);

  const prefixRef = useRef('');               // input text before dictation

  useEffect(() => {
    const hasRtc = typeof window !== 'undefined' && 'RTCPeerConnection' in window;
    const hasMr = typeof MediaRecorder !== 'undefined';
    const hasGum = !!navigator?.mediaDevices?.getUserMedia;
    if (!hasGum || (!hasRtc && !hasMr)) setUnsupported(true);
  }, []);

  // -------- helpers ----------
  const emitCombined = () => {
    const interim = Object.values(interimByItemRef.current).join(' ').trim();
    const speech = `${finalSoFarRef.current} ${interim}`.replace(/\s+/g, ' ').trim();
    const prefix = (prefixRef.current || '').trim();
    onUpdateRef.current?.(prefix ? `${prefix} ${speech}` : speech);
  };

  const releaseStream = () => {
    try { mediaStreamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    mediaStreamRef.current = null;
  };

  const tearDownRealtime = () => {
    try { dcRef.current?.close?.(); } catch (_) { /* noop */ }
    dcRef.current = null;
    try { pcRef.current?.getSenders?.().forEach((s) => { try { s.track?.stop?.(); } catch (_) {} }); } catch (_) { /* noop */ }
    try { pcRef.current?.close?.(); } catch (_) { /* noop */ }
    pcRef.current = null;
    releaseStream();
  };

  // -------- Realtime path --------
  const startRealtime = async () => {
    // 1. Mint ephemeral key.
    let token;
    {
      const r = await fetch('/api/voice/realtime-token', { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error(`token http ${r.status}`);
      const { clientSecret, model } = await r.json();
      token = { clientSecret, model };
    }

    // 2. Get mic + set up peer connection.
    const stream = await navigator.mediaDevices.getUserMedia({ audio: VOICE_AUDIO_CONSTRAINTS });
    mediaStreamRef.current = stream;
    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    pc.addTrack(stream.getAudioTracks()[0], stream);
    // Sink for the model's outbound audio (we don't want to hear it but
    // browsers require an audio sink or the connection negotiates weirdly).
    pc.ontrack = () => { /* discard model audio */ };

    const dc = pc.createDataChannel('oai-events');
    dcRef.current = dc;
    dc.addEventListener('open', () => {
      // Belt-and-suspenders session.update — server token mint already
      // configures the session, but resending here protects against drift if
      // the server config ever lags. Keep VAD ON so transcription actually
      // streams; `create_response: false` keeps the model silent.
      try {
        dc.send(JSON.stringify({
          type: 'session.update',
          session: {
            instructions: 'Transcribe only. Do not respond.',
            output_modalities: ['text'],
            audio: {
              input: {
                transcription: { model: 'whisper-1' },
                turn_detection: {
                  type: 'server_vad',
                  threshold: 0.5,
                  prefix_padding_ms: 300,
                  silence_duration_ms: 500,
                  create_response: false,
                  interrupt_response: false,
                },
              },
            },
          },
        }));
      } catch (_) { /* noop */ }
    });
    dc.addEventListener('message', (e) => {
      let event;
      try { event = JSON.parse(e.data); } catch (_) { return; }
      const t = event.type || '';

      // Interim transcript: 'conversation.item.input_audio_transcription.delta'
      if (t === 'conversation.item.input_audio_transcription.delta') {
        const itemId = event.item_id || event.itemId || 'cur';
        const prev = interimByItemRef.current[itemId] || '';
        interimByItemRef.current[itemId] = prev + (event.delta || '');
        emitCombined();
        return;
      }
      // Final transcript: 'conversation.item.input_audio_transcription.completed'
      if (t === 'conversation.item.input_audio_transcription.completed') {
        const itemId = event.item_id || event.itemId || 'cur';
        const text = (event.transcript || '').trim();
        delete interimByItemRef.current[itemId];
        if (text) {
          finalSoFarRef.current = `${finalSoFarRef.current} ${text}`.replace(/\s+/g, ' ').trim();
        }
        emitCombined();
        return;
      }
      // Surface unexpected error events to the console so we notice fast.
      if (t === 'error') {
        console.warn('[VoiceMicButton] realtime error event:', event.error || event);
      }
    });

    // 3. SDP offer → POST to OpenAI realtime/calls endpoint → set answer.
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const sdpRes = await fetch(
      `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(token.model)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token.clientSecret}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      }
    );
    if (!sdpRes.ok) {
      const body = await sdpRes.text();
      throw new Error(`sdp http ${sdpRes.status}: ${body.slice(0, 200)}`);
    }
    const answerSdp = await sdpRes.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    setState('recording');
    playUiTone('start');
  };

  // -------- Whisper REST fallback --------
  const startWhisperFallback = async () => {
    const mime = pickRecordMime();
    const stream = await navigator.mediaDevices.getUserMedia({ audio: VOICE_AUDIO_CONSTRAINTS });
    mediaStreamRef.current = stream;
    const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    recordedChunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      const blobType = recorder.mimeType || 'audio/webm';
      const blob = new Blob(recordedChunksRef.current, { type: blobType });
      recordedChunksRef.current = [];
      releaseStream();
      setState('transcribing');
      try {
        const ext = blobType.includes('mp4') ? 'm4a' : blobType.includes('ogg') ? 'ogg' : 'webm';
        const fd = new FormData();
        fd.append('file', blob, `dictation.${ext}`);
        fd.append('language', (lang || 'en-US').split('-')[0]);
        const r = await fetch('/api/voice/transcribe', { method: 'POST', body: fd, credentials: 'include' });
        if (r.ok) {
          const { text } = await r.json();
          const prefix = (prefixRef.current || '').trim();
          const combined = prefix ? `${prefix} ${(text || '').trim()}` : (text || '').trim();
          onUpdateRef.current?.(combined);
        }
      } catch (err) {
        console.warn('[VoiceMicButton] whisper fallback failed:', err?.message);
      } finally {
        setState('idle');
      }
    };
    recorder.start();
    recorderRef.current = recorder;
    setState('recording');
    playUiTone('start');
  };

  // -------- Click handler --------
  const start = async () => {
    prefixRef.current =
      (typeof getCurrentTextRef.current === 'function' ? getCurrentTextRef.current() : '') || '';
    interimByItemRef.current = {};
    finalSoFarRef.current = '';
    setState('connecting');
    try {
      await startRealtime();
    } catch (err) {
      console.warn('[VoiceMicButton] Realtime path failed, falling back to Whisper REST:', err?.message);
      tearDownRealtime();
      try {
        await startWhisperFallback();
      } catch (err2) {
        console.warn('[VoiceMicButton] Whisper fallback also failed:', err2?.message);
        releaseStream();
        setState('idle');
      }
    }
  };

  const stop = () => {
    // Realtime path: close peer connection. Any in-flight completed event will
    // arrive before close; deltas have already been emitted live.
    if (pcRef.current) {
      tearDownRealtime();
      setState('idle');
      playUiTone('stop');
      return;
    }
    // Whisper fallback path: stop the recorder; onstop handles transcription.
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) { /* noop */ }
      playUiTone('stop');
      return;
    }
    releaseStream();
    setState('idle');
    playUiTone('stop');
  };

  // Cleanup on unmount.
  useEffect(
    () => () => {
      tearDownRealtime();
      try { recorderRef.current?.stop?.(); } catch (_) { /* noop */ }
      releaseStream();
    },
    []
  );

  if (unsupported) {
    return (
      <button type="button" disabled title="Voice input not supported in this browser" className={`text-slate-400 cursor-not-allowed ${className}`}>
        <MicOff size={18} />
      </button>
    );
  }

  const onClick = () => {
    if (state === 'recording' || state === 'connecting') stop();
    else if (state === 'idle') start();
    // ignore clicks during 'transcribing' (Whisper fallback only)
  };

  const titleByState = {
    idle: 'Dictate your message',
    connecting: 'Connecting…',
    recording: 'Stop dictation',
    transcribing: 'Transcribing…',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={state === 'recording'}
      aria-label={titleByState[state]}
      title={titleByState[state]}
      disabled={state === 'transcribing'}
      className={`transition ${state === 'recording' ? 'text-red-600' : state === 'transcribing' || state === 'connecting' ? 'text-slate-400' : 'text-slate-500 hover:text-slate-800'} ${className}`}
    >
      {state === 'transcribing' || state === 'connecting'
        ? <Loader2 size={18} className="animate-spin" />
        : <Mic size={18} className={state === 'recording' ? 'animate-pulse' : ''} />}
    </button>
  );
}
